
// src/components/GlobalSearchModal.tsx
import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, BookOpen, Heart, Command } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSearch } from '@/contexts/SearchContext';
import { supabase } from '@/integrations/supabase/client';
import { callFunction } from '@/integrations/supabase/functions';

interface PlanEntry {
  name: string;
  passage: string;
  content?: string;
}

export function GlobalSearchModal() {
  const { open, query, results, isSearching, setOpen, setQuery } = useSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const [planEntries, setPlanEntries] = useState<PlanEntry[] | null>(null);
  const [planLoading, setPlanLoading] = useState(false);

  // Focus input on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Load reading plan when numeric query using centralized edge function
  useEffect(() => {
    const dayNum = parseInt(query, 10);
    if (!open || !query || isNaN(dayNum)) {
      setPlanEntries(null);
      return;
    }
    
    setPlanLoading(true);
    (async () => {
      try {
        const { data: planResults } = await callFunction('bible-search', {
          body: { 
            action: 'search', 
            query: query,
            searchContext: 'global'
          }
        });

        if ((planResults as any)?.success && (planResults as any).readingPlan) {
          setPlanEntries((planResults as any).readingPlan);
        } else {
          setPlanEntries([]);
        }
      } catch (error) {
        console.error('Failed to load reading plan:', error);
        setPlanEntries([]);
      } finally {
        setPlanLoading(false);
      }
    })();
  }, [query, open]);

  if (!open) return null;

  const handleResultClick = (res: any) => {
    setOpen(false);
    if (res.type === 'prayer') navigate(`/prayers?focus=${res.id}`);
    else if (res.type === 'chapter') navigate(`/bible?ref=${encodeURIComponent(res.reference)}&mode=chapter`);
    else navigate(`/bible?ref=${encodeURIComponent(res.reference)}`);
  };

  const handlePlanEntryClick = (entry: PlanEntry) => {
    setOpen(false);
    navigate(`/bible?ref=${encodeURIComponent(entry.passage)}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && results.length && planEntries === null) {
      e.preventDefault();
      handleResultClick(results[0]);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-start pt-20 px-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={() => setOpen(false)}
      >
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
          initial={{ opacity: 0, y: -20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center p-4 border-b">
            <Search className="h-5 w-5 text-gray-500" />
            <Input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search prayers, day #, Bible verses..."
              className="flex-1 mx-2 py-1 px-2"
            />
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              <X className="h-4 w-4 text-gray-500" />
            </Button>
          </div>
          {/* Body */}
          <div className="max-h-80 overflow-y-auto p-4">
            {!query ? (
              <div className="text-center text-gray-500">
                <p>Type a prayer topic, verse, or plan day.</p>
              </div>
            ) : planEntries !== null ? (
              <div className="space-y-4">
                <h3 className="font-medium">Reading Plan: Day {query}</h3>
                {planLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin h-5 w-5 border-2 border-t-indigo-500 rounded-full" />
                  </div>
                ) : planEntries.length === 0 ? (
                  <p className="text-gray-500">No reading plan found for day {query}.</p>
                ) : (
                  <div className="space-y-2">
                    {planEntries.map((entry, idx) => (
                      <motion.div
                        key={`${entry.name}-${idx}`}
                        className="p-3 rounded border hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        onClick={() => handlePlanEntryClick(entry)}
                      >
                        <div className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-blue-500" />
                          <div className="flex-1">
                            <div className="font-medium">{entry.passage}</div>
                            <div className="text-xs text-gray-500">{entry.name}</div>
                          </div>
                        </div>
                        {entry.content && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-3">
                            {entry.content}
                          </p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            ) : isSearching ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-t-indigo-500 rounded-full" />
              </div>
            ) : results.length ? (
              <div className="space-y-2">
                {results.map((res, i) => (
                  <motion.div
                    key={i}
                    className="p-2 rounded hover:bg-gray-100 cursor-pointer"
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => handleResultClick(res)}
                  >
                    <div className="flex items-center gap-2">
                      {res.type === 'prayer' && <Heart className="h-4 w-4 text-red-500" />}
                      {res.type === 'chapter' && <BookOpen className="h-4 w-4 text-blue-500" />}
                      <div>
                        <div className="font-medium truncate">{res.title}</div>
                        <p className="text-xs text-gray-500 line-clamp-2 mt-1">{res.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>No results found.</p>
              </div>
            )}
          </div>
          {/* Footer */}
          <div className="p-2 text-center text-xs text-gray-500 border-t">
            Press <kbd className="px-1 bg-gray-200 rounded text-[10px]">↵</kbd> to select, <kbd className="px-1 bg-gray-200 rounded text-[10px]">Esc</kbd> to close
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
